import React, {useCallback, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {DialogActions, DialogContent, Divider} from "@mui/material";
import {Col, Row} from "reactstrap";
import {OutlinedInput} from "../../../../base/input/outlined_input";
import {formatMoney} from "../../../../../../core/services/utils/utils";
import Button from "../../../../base/button/button";
import ReasonNoteSection from "../reason-note-section";
import Api from "../../../../../../core/services/api/api";

const reasonFormId = 'reason-form-id';

const ModifyProductAvailabilityDialogUnserializableSection = ({
                                                                  increase,
                                                                  partNo,
                                                                  close,
                                                                  setNewAvailability,
                                                                  currentAvailability,
                                                                  keyInformation
                                                              }) => {
    const [modifying, setModifying] = useState(false);
    const [cost, setCost] = useState('');
    const [count, setCount] = useState('');
    const isMounted = useIsMounted();

    /**@type {boolean}*/
    const canModify = useMemo(() => {
        if (increase) {
            return !!cost.length && !Number.isNaN(Number(cost)) && (Number(cost) >= 0);
        } else {
            return !Number.isNaN(Number(count)) && (Number(count) >= 0)
        }
    }, [cost, increase, count])

    /**
     * Modifies the availability of the provided un-serialized product in the provided warehouse.
     * @type {(function(values): Promise<void>)}
     */
    const modifyProductAvailability = useCallback(async (values) => {
        if (!canModify)
            return;
        setModifying(true);
        const forApi = {
            items: [{
                partNumber: partNo,
                partId: keyInformation.id,
                parentCategoryId: keyInformation.parentCategoryId,
                parentCategoryTitle: keyInformation.parentCategory,
                cost: Number(cost),
                quantity: Number(count),
                warehouseCode: keyInformation.warehouse,
                reasonId: values?.reason?.id ?? null,
                note: values?.note ?? null,
            }],
            isAdd: increase,
        }
        const response = await Api.createInventoryAdjustment(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            close();
            setNewAvailability(currentAvailability + (increase ? Number(count) : -(Number(count))));
        }
        setModifying(false);
    }, [canModify, close, cost, count, currentAvailability, increase, isMounted, keyInformation.id, keyInformation.parentCategory, keyInformation.parentCategoryId, keyInformation.warehouse, partNo, setNewAvailability])

    /**
     * Commits the change of the [cost] prop based on the type of the input value.
     * @type {(function({target: {value: *}}): (undefined|*))|*}
     */
    const onCostChangeCommitted = useCallback(({target: {value}}) => {
        if (!value) {
            return;
        }
        const num = Number(value.trim());
        if (Number.isNaN(num)) {
            return setCost('');
        }
        if (num < 0) {
            return setCost('0');
        }
        setCost(num.toString());
    }, [])

    /**
     * Commits the change of the [cost] prop based on the type of the input value.
     * @type {(function({target: {value: *}}): (undefined|*))|*}
     */
    const onCountChangeCommitted = useCallback(({target: {value}}) => {
        if (!value) {
            return;
        }
        const num = Number(value.trim());
        if (Number.isNaN(num)) {
            return setCount('');
        }
        if (num < 0) {
            return setCount('0');
        }
        if (!increase && num > currentAvailability) {
            return setCount(currentAvailability.toString());
        }
        setCount(num.toString());
    }, [currentAvailability, increase])

    /**
     * Callback function passed to the bottom section form to be called when the form is submitted
     * Passes the values of the form for the function for modifying the availabilities
     * @param values
     */
    const submitReasonForm = (values) => {
        modifyProductAvailability(values).then();
    }

    return (
        <>
            <DialogContent className={'modify-product-availability-dialog-content'}>
                {
                    increase &&
                    <>
                        <div className={'d-flex flex-column'}>
                            <div className={'d-flex w-100 titles-section'}>
                                <Col xs={4}>
                                    Current availability
                                </Col>
                                <Col xs={1}/>
                                <Col xs={3}>
                                    Adding items
                                </Col>
                                <Col xs={3}>
                                    Cost per item
                                </Col>
                            </div>
                            <div className={'d-flex w-100'}>
                                <Col xs={4}>
                                    <div className={'info-block'}>
                                        {currentAvailability}
                                    </div>
                                </Col>
                                <Col xs={1}>
                                    <p className={'math-icon'}>
                                        +
                                    </p>
                                </Col>
                                <Col xs={3}>
                                    <OutlinedInput
                                        value={count}
                                        type={'number'}
                                        onChange={setCount}
                                        inputProps={{min: 0}}
                                        onBlur={onCountChangeCommitted}
                                        disabled={modifying}
                                    />
                                </Col>
                                <Col xs={3}>
                                    <OutlinedInput
                                        value={cost}
                                        type={'number'}
                                        onChange={setCost}
                                        inputProps={{min: 0}}
                                        onBlur={onCostChangeCommitted}
                                        disabled={modifying}
                                    />
                                </Col>
                            </div>
                            <div className={'d-flex flex-column w-100 mt-4 justify-content-center align-items-center'}>
                                <div className={'d-flex align-items-center justify-content-center w-100 mb-3'}>
                                    <p className={'info-chips-title'}>
                                        New Availability
                                    </p>
                                    <div className={'info-chips'}>
                                        {(Number.isNaN(Number(count)) ? 0 : Number(count)) + (currentAvailability ?? 0)}
                                    </div>
                                </div>
                                <div className={'d-flex align-items-center justify-content-center w-100 mb-3'}>
                                    <p className={'info-chips-title mr-4'}>
                                        Average Cost
                                    </p>
                                    <div className={'info-chips ml-2'}>
                                        {formatMoney(keyInformation.averageCost * (increase ? 1 : -1))}
                                    </div>
                                </div>
                                <div className={'d-flex align-items-center justify-content-center w-100 mb-3'}>
                                    <p className={'info-chips-title mr-4'}>
                                        Extended Cost
                                    </p>
                                    <div className={'info-chips'}>
                                        {formatMoney((Number.isNaN(Number(count)) ? 0 : Number(count)) * (Number.isNaN(Number(cost)) ? 0 : Number(cost)))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                }
                {
                    !increase &&
                    <>
                        <div className={'d-flex flex-column'}>
                            <div className={'d-flex w-100  titles-section'}>
                                <Col xs={5}>
                                    Current availability
                                </Col>
                                <Col xs={4}>
                                    Removing items
                                </Col>
                                <Col xs={3}>
                                    Current Cost
                                </Col>

                            </div>
                            <div className={'d-flex w-100'}>
                                <Col xs={4}>
                                    <div className={'info-block'}>
                                        {currentAvailability}
                                    </div>
                                </Col>
                                <Col xs={1}>
                                    <p className={'math-icon'}>
                                        -
                                    </p>
                                </Col>
                                <Col xs={3}>
                                    <OutlinedInput
                                        value={count}
                                        type={'number'}
                                        onChange={setCount}
                                        inputProps={{min: 0, max: currentAvailability ?? 0}}
                                        onBlur={onCountChangeCommitted}
                                        disabled={modifying}
                                    />
                                </Col>
                                <Col xs={1}/>
                                <Col xs={3}>
                                    <OutlinedInput
                                        value={cost}
                                        type={'number'}
                                        onChange={setCost}
                                        inputProps={{min: 0}}
                                        onBlur={onCostChangeCommitted}
                                        disabled={modifying}
                                    />
                                </Col>
                            </div>
                            <div className={'d-flex flex-column w-100 mt-4 justify-content-center align-items-center'}>
                                <div className={'d-flex align-items-center justify-content-center w-100 mb-3'}>
                                    <p className={'info-chips-title'}>
                                        New Availability
                                    </p>
                                    <div className={'info-chips'}>
                                        {(currentAvailability ?? 0) - (Number.isNaN(Number(count)) ? 0 : Number(count))}
                                    </div>
                                </div>
                                <div className={'d-flex align-items-center justify-content-center w-100 mb-3'}>
                                    <p className={'info-chips-title mr-4'}>
                                        Average Cost
                                    </p>
                                    <div className={'info-chips ml-2'}>
                                        {formatMoney((Number.isNaN(Number(count)) ? 0 : Number(count)) * (Number.isNaN(Number(cost)) ? 0 : Number(cost)))}
                                    </div>
                                </div>
                                <div className={'d-flex align-items-center justify-content-center w-100 mb-3'}>
                                    <p className={'info-chips-title mr-4'}>
                                        Extended Cost
                                    </p>
                                    <div className={'info-chips'}>
                                        {`-${formatMoney((Number.isNaN(Number(count)) ? 0 : Number(count)) * (Number.isNaN(Number(cost)) ? 0 : Number(cost)))}`}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                }
                <ReasonNoteSection
                    formId={reasonFormId}
                    onSubmit={submitReasonForm}
                    submitting={modifying}
                />
            </DialogContent>
            <DialogActions className={'p-0 d-flex flex-column'}>
                <Divider className='divider'/>
                <Row className='no-gutters justify-content-between align-items-start w-100  m-0'>
                    <Button
                        disabled={modifying}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={close}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={modifying || !canModify}
                        variant={'dialog'}
                        color={'primary'}
                        type={"submit"}
                        form={reasonFormId}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {modifying ? "Processing..." : "Finalize"}
                    </Button>
                </Row>
            </DialogActions>
        </>
    );
}

export default ModifyProductAvailabilityDialogUnserializableSection;
